import { chipInkForFill } from "../lib/chip-contrast.ts";
import { TypeIcon } from "./type-icon.tsx";

/**
 * Grade visual de tipo de entrega.
 *
 * Cada tipo vira um cartão com o ícone filled correspondente. A escolha aqui é
 * a mesma chave que o calendário usa no chip — criar "Vídeo / Reels" precisa
 * aparecer como o ícone de reels, não como um rótulo genérico.
 */

export interface TypePickerType {
  id: string;
  label: string;
  color: string;
  slug: string;
  icon: string | null;
}

export interface TypePickerProps {
  id: string;
  types: TypePickerType[];
  value: string;
  onChange: (typeId: string) => void;
}

export function TypePicker({ id, types, value, onChange }: TypePickerProps) {
  return (
    <div
      id={id}
      role="radiogroup"
      aria-label="Tipo"
      className="dream-scroll grid max-h-[min(13rem,38dvh)] grid-cols-2 gap-1.5 overflow-y-auto pr-0.5"
    >
      {types.map((type) => {
        const selected = type.id === value;
        return (
          <label
            key={type.id}
            className={[
              "flex min-h-9 cursor-pointer items-center gap-2 rounded-lg border px-2 py-1.5 text-left",
              "transition-[border-color,background,box-shadow,transform] duration-[var(--dream-duration-fast)] ease-[var(--dream-ease-out)]",
              "focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-brand-500",
              selected
                ? "border-[var(--color-brand-500)] bg-brand-50 shadow-[0_0_0_3px_rgb(0_116_255/0.16)]"
                : "border-[var(--color-line)] bg-[var(--color-surface)] hover:-translate-y-px hover:border-[var(--color-ink-subtle)] hover:bg-[var(--color-surface-muted)] hover:shadow-[var(--shadow-xs)]",
            ].join(" ")}
          >
            <input
              type="radio"
              name={id}
              value={type.id}
              checked={selected}
              onChange={() => onChange(type.id)}
              className="sr-only"
            />
            <span
              className="pointer-events-none flex size-7 shrink-0 items-center justify-center rounded-md"
              style={{ backgroundColor: type.color, color: chipInkForFill(type.color).ink }}
              aria-hidden="true"
            >
              <TypeIcon slug={type.slug} name={type.icon} size={14} />
            </span>
            <span
              className={[
                "min-w-0 flex-1 text-[11px] leading-4",
                selected
                  ? "font-semibold text-[var(--color-brand-500)]"
                  : "font-medium text-[var(--color-ink)]",
              ].join(" ")}
            >
              {type.label}
            </span>
          </label>
        );
      })}
    </div>
  );
}
